import React, { useEffect, useState } from 'react';
import { transitionAnimation } from 'lib/core';
import { NestedList, StyledNestedListItem } from 'lib/list';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { NavListItemsCollapsed, NavListItemsExpanded } from '../elements';
import { customNavScrollbar } from '../shared-styles';

const ListItemContainer = styled.div`
  ${transitionAnimation('width')};
  ${customNavScrollbar};
  background-color: ${({ theme }) => theme.color.gray[50].value};
  display: flex;
  flex-direction: column;
  ${StyledNestedListItem} button a {
    padding: 0;
    margin: 0;
    text-decoration: none;
    height: auto;
    display: flex;
    background: inherit;
    transition: none;
  }
`;

const NavigationMenu = React.forwardRef(
  (
    {
      className = 'nav-menu',
      dataTestId = undefined,
      expandedItemIds = {},
      isCollapsed = false,
      items,
      onExpand,
      onItemsExpandCollapse,
    },
    ref,
  ) => {
    const [activeItemId, setActiveItemId] = useState('');

    const itemHasChildren = (arr, targetId) => {
      for (const item of arr) {
        // Check if the current item is the target
        if (item.id === targetId) {
          return 'items' in item && item.items.length > 0;
        }

        // If the current item has children, recursively check them
        if ('items' in item && item.items.length > 0) {
          const foundInChild = itemHasChildren(item.items, targetId);
          if (foundInChild) {
            return true;
          }
        }
      }

      // Return false if the targetId is not found in the array or any of its sub-arrays
      return false;
    };

    const getActiveItem = (items, activeItemId) => {
      for (const item of items) {
        // Check if the current item is the target
        if (item.id === activeItemId) {
          return item;
        }
        // recursively check them
        if ('items' in item && item.items.length > 0) {
          const foundInChild = getActiveItem(item.items, activeItemId);
          if (foundInChild) {
            return foundInChild;
          }
        }
      }
      return null;
    };

    const handleClick = (event) => {
      let element = event.target;

      while (element.nodeName !== 'BUTTON' && element.nodeName !== 'A' && element !== event.currentTarget) {
        element = element.parentNode;
      }
      if (!itemHasChildren(items, element.id)) {
        setActiveItemId(element.id);
        const activeItem = getActiveItem(items, element.id);
        const rdsNavMenuSelect = new CustomEvent('rdsNavMenuSelect', {
          detail: {
            activeItem: activeItem,
            ref: ref,
          },
          bubbles: true,
        });
        document.dispatchEvent(rdsNavMenuSelect);
      }
    };

    /**
     * Listens for the rdsSetNavMenuHighlight CustomEvent, and clears the menu highlight
     * by setting the activeItemId to an empty string (its initial state) OR
     * sets the menu highlight to the item with a specific activeItemId
     * if it is passed in from the application's CustomEvent
     */
    useEffect(() => {
      const setMenuHighlight = (event) => {
        setActiveItemId(event?.detail?.activeItemId || '');
      };

      document.addEventListener('rdsSetNavMenuHighlight', setMenuHighlight);
      return () => {
        document.removeEventListener('rdsSetNavMenuHighlight', setMenuHighlight);
      };
    }, []);

    return (
      <ListItemContainer className={className} onClick={handleClick} ref={ref}>
        {isCollapsed ? (
          <NavListItemsCollapsed
            activeItemId={activeItemId}
            className={`${className}-menu-collapsed`}
            dataTestId={dataTestId}
            expandedItemIds={expandedItemIds}
            items={items}
            onExpand={onExpand}
            onItemExpandCollapse={onItemsExpandCollapse}
          />
        ) : (
          <NavListItemsExpanded
            activeItemId={activeItemId}
            className={`${className}-menu-expanded`}
            dataTestId={dataTestId}
            expandedItemIds={expandedItemIds}
            items={items}
            onItemExpandCollapse={onItemsExpandCollapse}
          />
        )}
      </ListItemContainer>
    );
  },
);

NavigationMenu.propTypes = {
  /** Adds additional class to the component */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Item ids that are currently expanded. This prop consists of object entries, where key is the id of the item
   * and value is true if item is expanded and falsy (false, null, undefined) if it is not */
  expandedItemIds: PropTypes.shape({}),
  /** If true, Menu is collapsed horizontally into a column of icons */
  isCollapsed: PropTypes.bool,
  /** Items of navigation menu */
  items: PropTypes.arrayOf(PropTypes.shape({ ...NestedList.propTypes.items })).isRequired,
  /** Callback for Menu horizontal expand from icons to List items */
  onExpand: PropTypes.func.isRequired,
  /** Callback for Menu List items to vertically expand or collapse */
  onItemsExpandCollapse: PropTypes.func.isRequired,
};

export { NavigationMenu };
